import React, { useState, useEffect, useCallback } from 'react';
import Flatpickr from 'react-flatpickr';
import useAutoSave from '../../shared/components/useAutoSave';
import CollapsibleSection from '../../shared/components/CollapsibleSection';
import CrmCustomFieldsSection from '../../shared/components/CrmCustomFieldsSection';
import { canSubmitCustomFieldValue } from '../../CustomFieldValues/actions/customFieldValuesActionCreators';
import useCustomFieldValues from '../../shared/hooks/useCustomFieldValues';

const EVENTS_ICON = '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>';
const NOTES_ICON = '<path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>';

function TaskFormModal({
  isOpen,
  onClose,
  task,
  team,
  csrfToken,
  statuses = [],
  assignees = [],
  events = [],
  customFieldDefinitions = [],
  onSaved,
}) {
  const isEditing = !!(task && task.id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('open');
  const [assigneeId, setAssigneeId] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [calendarEventId, setCalendarEventId] = useState('');
  const { customFieldValues, handleCustomFieldChange, buildCustomFieldAttributes, resetCustomFieldValues, syncCustomFieldRowIds } = useCustomFieldValues(customFieldDefinitions, task ? task.custom_field_values : []);

  const handleSaved = useCallback((saved, isNew) => {
    syncCustomFieldRowIds(saved.custom_field_values);
    if (onSaved) onSaved(saved, isNew);
  }, [onSaved]);

  const { recordId, saveStatus, errors, saveFields } = useAutoSave({
    team,
    csrfToken,
    resourcePath: 'crm/tasks',
    resourceKey: 'task',
    existingId: isEditing ? task.id : null,
    isOpen,
    onSaved: handleSaved,
  });

  useEffect(() => {
    if (!isOpen) return;

    // The modal stays mounted across open/close; re-init custom field state so
    // multi-association row ids don't go stale after a save.
    resetCustomFieldValues(task && task.id ? task.custom_field_values : []);

    if (task && task.id) {
      setTitle(task.title || '');
      setDescription(task.description || '');
      setStatus(task.status || 'open');
      setAssigneeId(task.assignee_team_membership_id ? String(task.assignee_team_membership_id) : '');
      setDueAt(task.due_at ? task.due_at.substring(0, 16) : '');
      setCalendarEventId(task.calendar_event_id ? String(task.calendar_event_id) : '');
    } else {
      setTitle('');
      setDescription('');
      setStatus('open');
      setAssigneeId('');
      setDueAt('');
      setCalendarEventId('');
    }
  }, [isOpen, task]);

  if (!isOpen) return null;

  const canSubmitForm = title.trim().length > 0 && customFieldValues.every((cfv) => canSubmitCustomFieldValue(cfv));

  const buildPayload = () => ({
    title,
    description,
    status,
    due_at: dueAt || null,
    assignee_team_membership_id: assigneeId || null,
    calendar_event_id: calendarEventId || null,
    custom_field_values_attributes: buildCustomFieldAttributes(),
  });

  const handleClose = () => {
    if (saveStatus !== 'saving') onClose();
  };

  const handleSubmit = () => {
    if (!canSubmitForm || saveStatus === 'saving') return;
    saveFields(buildPayload(), { afterSave: onClose });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const getStatusLabel = (s) => {
    switch (s) {
      case 'open': return 'Open';
      case 'in_progress': return 'In-Progress';
      case 'done': return 'Done';
      default: return s.replace('_', ' ');
    }
  };

  const getEventLabel = (eventId) => {
    if (!eventId) return '';
    const match = events.find((e) => String(e.id) === String(eventId));
    if (!match) return '';
    const parts = [match.title];
    if (match.venue_name) parts.push(match.venue_name);
    if (match.calendar_classification) parts.push(match.calendar_classification);
    return parts.filter(Boolean).join(' - ');
  };

  const saveStatusLabel = {
    idle: null,
    saving: 'Saving...',
    saved: 'Saved',
    error: 'Error saving',
  }[saveStatus];

  const saveStatusClass = {
    idle: '',
    saving: 'cfm-save-status--saving',
    saved: 'cfm-save-status--saved',
    error: 'cfm-save-status--error',
  }[saveStatus];

  const breadcrumbLabel = title.trim() ? title.trim() : (isEditing ? 'Edit Task' : 'Add Task');

  return (
    <div className="cfm-overlay" onClick={handleOverlayClick}>
      <div className="cfm-modal" role="dialog" aria-label={breadcrumbLabel}>
        <div className="cfm-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className="cfm-breadcrumb">
              <span>Tasks&nbsp;/&nbsp;</span>{breadcrumbLabel}
            </div>
            {saveStatusLabel && (
              <span className={`cfm-save-status ${saveStatusClass}`}>{saveStatusLabel}</span>
            )}
          </div>
          <button className="cfm-close" onClick={handleClose} aria-label="Close">&times;</button>
        </div>

        {errors.length > 0 && (
          <div className="cfm-errors">
            <ul>{errors.map((err) => <li key={err}>{err}</li>)}</ul>
          </div>
        )}

        <div className="cfm-body">
          <div className="cfm-pane-main">

            <div className="cfm-field">
              <label className="cfm-label">Title <span className="required">*</span></label>
              <div className="cfm-input-wrap">
                <input
                  className="cfm-input"
                  type="text"
                  placeholder="Task title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
            </div>


            <div className="cfm-field">
              <label className="cfm-label">Due date</label>
              <div className="cfm-input-wrap">
                <svg className="cfm-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <Flatpickr
                  className="cfm-input"
                  options={{ enableTime: true, dateFormat: 'Y-m-d\\TH:i', altInput: true, altFormat: 'n/j/Y \\a\\t h:iK', altInputClass: 'cfm-input' }}
                  placeholder="Select date / time"
                  value={dueAt || ''}
                  onClose={(dates) => {
                    if (dates[0]) {
                      const d = dates[0];
                      const year = d.getFullYear();
                      const month = String(d.getMonth() + 1).padStart(2, '0');
                      const day = String(d.getDate()).padStart(2, '0');
                      const hours = String(d.getHours()).padStart(2, '0');
                      const mins = String(d.getMinutes()).padStart(2, '0');
                      setDueAt(`${year}-${month}-${day}T${hours}:${mins}`);
                    }
                  }}
                />
              </div>
            </div>


            <div className="cfm-field">
              <label className="cfm-label">Assignee</label>
              <div className="cfm-select-wrap">
                <select
                  className={`cfm-select${!assigneeId ? ' cfm-select--placeholder' : ''}`}
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                >
                  <option value="">Not Assigned</option>
                  {assignees.map((a) => (
                    <option key={a.id} value={String(a.id)}>{a.name}</option>
                  ))}
                </select>
              </div>
            </div>


            <CollapsibleSection title="Events" icon={EVENTS_ICON} defaultExpanded={!!calendarEventId}>
              <div className="cfm-field">
                <label className="cfm-label">Event</label>
                <div className="cfm-select-wrap">
                  <select
                    className={`cfm-select${!calendarEventId ? ' cfm-select--placeholder' : ''}`}
                    value={calendarEventId}
                    onChange={(e) => setCalendarEventId(e.target.value)}
                  >
                    <option value="">Not Assigned</option>
                    {events.map((ev) => (
                      <option key={ev.id} value={String(ev.id)}>{getEventLabel(ev.id)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CollapsibleSection>

            <CrmCustomFieldsSection
              team={team}
              customFieldDefinitions={customFieldDefinitions}
              customFieldValues={customFieldValues}
              onCustomFieldChange={handleCustomFieldChange}
            />

            <CollapsibleSection title="Notes" icon={NOTES_ICON} defaultExpanded={!!description}>
              <div className="cfm-field">
                <label className="cfm-label">Description</label>
                <div className="cfm-textarea-wrap">
                  <textarea
                    className="cfm-textarea"
                    placeholder="Add notes about this task..."
                    rows="3"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </div>
            </CollapsibleSection>
          </div>


          <div className="cfm-pane-sidebar">
            <div className="cfm-prop-row">
              <span className="cfm-prop-label">Created by</span>
              <span className="cfm-prop-value-static">You</span>
            </div>

            <div className="cfm-prop-row">
              <span className="cfm-prop-label">Status</span>
              <select
                className="cfm-prop-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>{getStatusLabel(s)}</option>
                ))}
              </select>
            </div>

            <div className="cfm-sidebar-divider" />
            <div className="cfm-sidebar-section-label">Record Info</div>

            <div className="cfm-prop-row">
              <span className="cfm-prop-label">Created</span>
              <span className="cfm-prop-value-static">
                {task && task.created_at
                  ? new Date(task.created_at).toLocaleDateString()
                  : 'Now'}
              </span>
            </div>

            <div className="cfm-prop-row">
              <span className="cfm-prop-label">Modified</span>
              <span className="cfm-prop-value-static">
                {task && task.updated_at
                  ? new Date(task.updated_at).toLocaleDateString()
                  : 'Now'}
              </span>
            </div>
          </div>
        </div>

        <div className="cfm-footer">
          <button
            type="button"
            className="btn btn-setlist-primary"
            disabled={!canSubmitForm || saveStatus === 'saving'}
            onClick={handleSubmit}
          >
            {saveStatus === 'saving' ? 'Saving...' : 'Save Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TaskFormModal;
